/** City / Building construction + structural queries. The City owns Buildings;
 *  each Building is a tower (floors, rooms, crew, larder, heat). */
import type { City, Building, Room, RoomType, Colonist, CityPlot, Toast } from './types';
import { SIM, CITY_PLOTS, SIZE_FLOORS } from './config';
import { Rng, seedFromTime } from './rng';
import { makeColonist } from './colonist';

export const SAVE_VERSION = 3;

/** Horizontal position (0..1) of a room slot; the elevator occupies x≈0. */
export function slotX(slot: number, slots = SIM.SLOTS_PER_FLOOR): number {
  return 0.22 + ((slot + 0.5) / slots) * 0.76;
}

// --- building queries --------------------------------------------------------
export function findRoom(b: Building, id: number | null): Room | undefined {
  if (id == null) return undefined;
  return b.rooms.find((r) => r.id === id);
}
export function roomAt(b: Building, floor: number, slot: number): Room | undefined {
  return b.rooms.find((r) => r.floor === floor && r.slot === slot);
}
export function assignedTo(b: Building, roomId: number): Colonist[] {
  return b.colonists.filter((c) => c.assignedRoomId === roomId);
}
export function roomsOfType(b: Building, type: RoomType): Room[] {
  return b.rooms.filter((r) => r.type === type);
}
export function floorCount(b: Building): number {
  return b.floorMax - b.floorMin + 1;
}
export function floorCost(b: Building): number {
  return Math.round(SIM.FLOOR_COST_BASE * Math.pow(SIM.FLOOR_COST_GROWTH, floorCount(b) - SIM.STARTING_FLOORS));
}
export function maxCrewFor(b: Building): number {
  return 4 + Math.max(0, floorCount(b) - 3);
}

// --- city queries ------------------------------------------------------------
export function activeBuilding(city: City): Building | undefined {
  return city.buildings.find((b) => b.id === city.activeId) ?? city.buildings[0];
}
export function buildingById(city: City, id: number): Building | undefined {
  return city.buildings.find((b) => b.id === id);
}

// --- construction ------------------------------------------------------------
function starterRooms(): { rooms: Room[]; nextId: number } {
  const rooms: Room[] = [];
  let id = 1;
  const add = (type: RoomType, floor: number, slot: number) =>
    rooms.push({ id: id++, type, floor, slot, tier: 1, damagedUntil: 0, progress: 0 });
  add('grow', -1, 0); // basement cultivation
  add('deal', 0, 0); // street drop
  add('bunk', 1, 0); // floors: quarters
  add('diner', 1, 1);
  add('rec', 1, 2);
  return { rooms, nextId: id };
}

export function createBuilding(rng: Rng, id: number, plot: CityPlot, crewCount: number): Building {
  const sz = SIZE_FLOORS[plot.size];
  const { rooms, nextId } = starterRooms();
  const colonists: Colonist[] = [];
  for (let i = 0; i < crewCount; i++) colonists.push(makeColonist(rng, i + 1));
  const grow = rooms.find((r) => r.type === 'grow')!;
  const deal = rooms.find((r) => r.type === 'deal')!;
  const bunk = rooms.find((r) => r.type === 'bunk');
  if (colonists[0]) colonists[0].assignedRoomId = grow.id;
  if (colonists[1]) colonists[1].assignedRoomId = deal.id;
  for (const c of colonists) c.homeBunkId = bunk?.id ?? null;

  const b: Building = {
    id,
    name: plot.name,
    district: plot.district,
    mapX: plot.mapX,
    mapY: plot.mapY,
    size: plot.size,
    art: plot.art,
    floorMin: sz.min,
    floorMax: sz.max,
    rooms,
    nextRoomId: nextId,
    colonists,
    nextColonistId: crewCount + 1,
    maxCrew: sz.crew,
    res: { product: 0, meals: 12 },
    heat: 0,
    layingLow: false,
    security: {},
    raid: null,
    raidTimer: SIM.RAID_ROLL_SEC,
  };
  b.maxCrew = maxCrewFor(b);
  return b;
}

export function createCity(now: number): City {
  const rng = new Rng(seedFromTime(now));
  return {
    version: SAVE_VERSION,
    t: 0,
    speed: 1,
    cash: SIM.STARTING_CASH,
    rep: 0,
    runEarned: 0,
    buildings: [],
    plots: CITY_PLOTS.map((p) => ({ ...p })),
    activeId: 0,
    nextBuildingId: 1,
    eventTimer: SIM.EVENT_INTERVAL_SEC,
    activeEvents: [],
    recruit: null,
    completedGoals: [],
    rngSeed: rng.seed,
    lastSeen: now,
    log: [{ t: 0, day: 1, kind: 'info', text: 'Pick your first building on the city map — start with the cheapest.' }],
    toasts: [],
    nextToastId: 1,
    raidsSurvived: 0,
    raidsFaced: 0,
    arrested: 0,
  };
}

// --- clone / log -------------------------------------------------------------
function cloneSkills(s: Colonist['skills']): Colonist['skills'] {
  const out = {} as Colonist['skills'];
  for (const k in s) out[k as keyof typeof s] = { ...s[k as keyof typeof s] };
  return out;
}
function cloneColonist(c: Colonist): Colonist {
  return {
    ...c,
    skills: cloneSkills(c.skills),
    needs: { ...c.needs },
    traits: c.traits.slice(),
    recentThoughts: c.recentThoughts.map((t) => ({ ...t })),
  };
}
function cloneBuilding(b: Building): Building {
  return {
    ...b,
    rooms: b.rooms.map((r) => ({ ...r })),
    colonists: b.colonists.map(cloneColonist),
    security: { ...b.security },
    res: { ...b.res },
    raid: b.raid ? { ...b.raid } : null,
  };
}
export function cloneCity(city: City): City {
  return {
    ...city,
    buildings: city.buildings.map(cloneBuilding),
    plots: city.plots.map((p) => ({ ...p })),
    activeEvents: city.activeEvents.map((e) => ({ ...e })),
    recruit: city.recruit ? { ...city.recruit, colonist: cloneColonist(city.recruit.colonist) } : null,
    completedGoals: city.completedGoals.slice(),
    log: city.log.slice(-60),
    toasts: city.toasts.map((t) => ({ ...t })),
  };
}

export function pushLog(city: City, kind: City['log'][number]['kind'], text: string): void {
  city.log.push({ t: city.t, day: Math.floor(city.t / SIM.DAY_SEC) + 1, kind, text });
  if (city.log.length > 60) city.log.shift();
}

/** A transient notice for a building the player isn't currently managing —
 *  surfaced as a dismissable toast (see AppSim.svelte); self-expires after `ttlSec`. */
export function pushToast(city: City, buildingId: number, kind: Toast['kind'], text: string, ttlSec: number): void {
  city.toasts.push({ id: city.nextToastId++, kind, text, buildingId, expiresAt: city.t + ttlSec });
  if (city.toasts.length > 4) city.toasts.shift();
}
